import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { BackBar } from "../../components/BackBar";
import { toast } from "sonner";
import { Upload, Check } from "lucide-react";

export default function EvaluationPage() {
  const { id } = useParams();
  const { lang } = useLang();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/applications/${id}`).then(r => setApp(r.data)).catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [id]);

  const onFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 2_500_000) { toast.error("Max 2.5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, base64: reader.result });
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!file) { toast.error(lang === "fr" ? "Veuillez joindre votre évaluation complétée" : "Please attach your completed evaluation"); return; }
    setSubmitting(true);
    try {
      await api.post(`/applications/${id}/evaluation/submit`, { filename: file.name, base64: file.base64, notes });
      toast.success(lang === "fr" ? "Évaluation transmise — merci !" : "Evaluation submitted — thank you!");
      nav("/parent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  if (!app) return <div className="p-12">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto px-6 py-8" data-testid="evaluation-page">
      <BackBar to="/parent" fallbackLabelFr="Tableau de bord" fallbackLabelEn="Dashboard" />
      <p className="overline mb-2">{lang === "fr" ? "Évaluation" : "Evaluation"}</p>
      <h1 className="font-display font-bold text-4xl tracking-tighter mb-8">
        {lang === "fr" ? "Téléverser mon évaluation" : "Upload my evaluation"}
      </h1>
      {app.evaluation?.message && (
        <Card className="mb-6 border-2 border-primary/30 bg-primary/5">
          <CardHeader><CardTitle className="text-base">{lang === "fr" ? "Consignes" : "Instructions"}</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-line">{app.evaluation.message}</p>
            {app.evaluation.filename && app.evaluation.base64 && (
              <a href={app.evaluation.base64} download={app.evaluation.filename} className="inline-flex items-center gap-1 mt-3 text-sm text-primary underline" data-testid="download-eval">
                📄 {app.evaluation.filename}
              </a>
            )}
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <Label className="text-sm">{lang === "fr" ? "Votre évaluation complétée (PDF ou image, max 2,5 Mo)" : "Your completed evaluation (PDF or image, max 2.5MB)"}</Label>
            <label className="block mt-2 border-2 border-dashed border-border rounded-lg p-6 cursor-pointer hover:border-primary transition-colors text-center" data-testid="eval-upload-zone">
              {file ? (
                <div className="flex items-center justify-center gap-2 text-sm"><Check className="w-4 h-4 text-accent" /> {file.name}</div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground"><Upload className="w-5 h-5" />{lang === "fr" ? "Cliquer pour téléverser" : "Click to upload"}</div>
              )}
              <input type="file" className="hidden" onChange={onFile} accept="application/pdf,image/*" data-testid="eval-upload-input" />
            </label>
          </div>
          <div>
            <Label className="text-sm">{lang === "fr" ? "Notes (optionnel)" : "Notes (optional)"}</Label>
            <Textarea data-testid="eval-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <Button data-testid="eval-submit" onClick={submit} disabled={submitting || !file} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full">
            {submitting ? "…" : (lang === "fr" ? "Envoyer mon évaluation" : "Submit my evaluation")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
